import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createUseStyles, Divider, Icon, Input, Select, Text, useTheme } from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import classnames from 'classnames';

import { getCrossID } from 'utils/filters';

import { operators } from '../../operators';
import styles from './styles';
const { EMPTY, NOT_EMPTY } = operators;

const useStyles = createUseStyles(styles, 'Products');

const Products = ({ crossEntity, field, getRef, onChange, operator, values, isQuickfilter }) => {
    const [productValues, setProductValues] = useState([]);
    const [quantityValues, setQuantityValues] = useState([]);
    const classes = useStyles();
    const theme = useTheme();
    const valuesRef = useRef({ productValues: [], quantityValues: [] });
    const isFirstLoad = useRef(true);

    const { id, inputProps, extraFilterProps } = field;

    const { PID, QID } = useMemo(
        () => ({
            PID: getCrossID(id, crossEntity),
            QID: getCrossID(extraFilterProps.id, crossEntity),
        }),
        [crossEntity, extraFilterProps.id, id],
    );

    const handleValues = useCallback(
        (values) => {
            const newProductValues = values[PID] || [];
            const newQuantityValues = Array.isArray(values[QID])
                ? values[QID].map((q) => ({
                      value: q.value || '',
                      label: q.label || undefined,
                      idParent: q.idParent || undefined,
                  }))
                : !!values[QID]
                  ? [{ value: values[QID], label: undefined, idParent: undefined }]
                  : [];

            setProductValues(newProductValues);
            setQuantityValues(newQuantityValues);

            valuesRef.current = {
                productValues: newProductValues,
                quantityValues: newQuantityValues,
            };
        },
        [PID, QID],
    );

    const handleOnChange = useCallback(
        (updatedProductValues, updatedQuantityValues) => {
            if ([EMPTY, NOT_EMPTY].includes(operator)) {
                updatedProductValues = [];
                updatedQuantityValues = [];
            }
            onChange({ field, crossEntity })(updatedProductValues);
            onChange({ field: field.extraFilterProps, crossEntity })(updatedQuantityValues);
        },
        [crossEntity, field, onChange, operator],
    );

    useEffect(() => {
        const productValues = values[PID] || [];
        const quantityValues = values[QID] || [];

        if (isFirstLoad.current) {
            handleValues(values);
            isFirstLoad.current = false;
            return;
        }

        if (
            isEqual(valuesRef.current.productValues, productValues) &&
            isEqual(valuesRef.current.quantityValues, quantityValues)
        ) {
            return;
        }

        handleValues(values);
    }, [PID, QID, handleValues, values]);

    const onChangeSearch = useCallback(
        (products) => {
            if (!products || !Array.isArray(products)) {
                handleOnChange([], []);
            } else {
                const updatedQuantityValues = products.reduce((arr, item) => {
                    const currentQuantity = quantityValues.find(
                        (quantity) => quantity.label === item.value,
                    );
                    arr.push(
                        currentQuantity || { value: '', label: item.value, idParent: undefined },
                    );
                    return arr;
                }, []);

                handleOnChange(products, updatedQuantityValues);
            }
        },
        [handleOnChange, quantityValues],
    );

    const onChangeQuantity = useCallback(
        ({ index, quantity: newQuantity, inner = false }) => {
            const updatedQuantityValues = quantityValues.map((quantityObj, i) =>
                i === index ? { ...quantityObj, value: newQuantity } : quantityObj,
            );

            setQuantityValues(updatedQuantityValues);

            (!inner || !newQuantity) && handleOnChange(productValues, updatedQuantityValues);
        },
        [productValues, quantityValues, handleOnChange],
    );

    const onDeleteRow = useCallback(
        (index) => {
            const updatedProductValues = productValues.filter((_, i) => i !== index);
            const updatedQuantityValues = quantityValues.filter((_, i) => i !== index);
            handleOnChange(updatedProductValues, updatedQuantityValues);
        },
        [productValues, quantityValues, handleOnChange],
    );

    /** Same structure as QuickFilters `selectOverrides` (fuzzy value-list Select inside popover). */
    const filterMenuOverrides = useMemo(
        () => ({
            styles: {
                menu: (base) => ({
                    ...base,
                    position: 'relative',
                    boxShadow: 'none !important',
                    margin: 0,
                    paddingBottom: 0,
                }),
                menuList: (base) => ({
                    ...base,
                    paddingBottom: 0,
                    ...theme.utils.scrollbar,
                }),
            },
            option: {
                style: {
                    borderRadius: 4,
                },
            },
        }),
        [theme],
    );

    const selectProps = useMemo(() => {
        const {
            overrides: inputOverrides,
            className: inputClassName,
            ...restInputProps
        } = inputProps;

        let finalOverrides = inputOverrides;
        if (isQuickfilter) {
            finalOverrides = filterMenuOverrides;
            if (inputOverrides) {
                finalOverrides = { ...filterMenuOverrides, ...inputOverrides };
            }
        }

        return {
            ...restInputProps,
            ...(finalOverrides !== undefined ? { overrides: finalOverrides } : {}),
            onChange: onChangeSearch,
            isReadOnly: [EMPTY, NOT_EMPTY].includes(operator),
            forceBlurOnEnter: false,
            useMenuPortal: !isQuickfilter,
            ...(isQuickfilter ? { menuPosition: 'absolute' } : {}),
            isMulti: true,
            value: productValues,
            isFullWidth: true,
            highlightMatch: inputProps.isFuzzy,
            withoutFilter: true,
            inputProps: { name: 'search' },
            className: isQuickfilter
                ? classnames(classes.selectFuzzy, inputClassName)
                : inputClassName,
        };
    }, [
        classes.selectFuzzy,
        filterMenuOverrides,
        inputProps,
        onChangeSearch,
        operator,
        isQuickfilter,
        productValues,
    ]);

    const renderRows = useMemo(() => {
        if (productValues.length === 0) return null;

        const rows = productValues.reduce(
            (arr, product, index) => {
                arr.push(
                    <div key={`product-${product.value}-${index}`} className={classes.row}>
                        <div className={classes.badgeWrap}>
                            <Text type="body">{product.label}</Text>
                        </div>
                        <Text className={classes.sep}>-</Text>
                        <div className={classes.inputWrap}>
                            <Input
                                isFullWidth
                                type="integer"
                                value={quantityValues[index]?.value}
                                onChange={(quantity) =>
                                    onChangeQuantity({ index, quantity, inner: true })
                                }
                                onBlur={(quantity) => onChangeQuantity({ index, quantity })}
                                placeholder="0"
                            />
                        </div>
                        <Icon name="delete" onClick={() => onDeleteRow(index)} />
                    </div>,
                );
                return arr;
            },
            [<Divider key="divider" className={classes.divider} />],
        );
        return rows;
    }, [
        classes.badgeWrap,
        classes.divider,
        classes.inputWrap,
        classes.row,
        classes.sep,
        onChangeQuantity,
        onDeleteRow,
        productValues,
        quantityValues,
    ]);

    return (
        <div className={classes.root}>
            <Select {...selectProps} getRef={getRef} />
            {renderRows}
        </div>
    );
};

export default memo(Products);
